import { z } from 'zod';
import { baseString } from './core/primitives';
import { str } from './core/helpers';

// ── Constants ───────────────────────────────────────────────────────────────
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;

// ── Field definitions ───────────────────────────────────────────────────────

/**
 * Strong password with complexity rules (lowercase, uppercase, digit, special).
 * Exported for reuse in Admin/Users form.
 */
export const strongPassword = (t, fieldKey = 'password') =>
  z.preprocess(
    str,
    z
      .string()
      .min(1, t(`validation.${fieldKey}.required`))
      .min(
        PASSWORD_MIN,
        t(`validation.${fieldKey}.minLength`, { min: PASSWORD_MIN })
      )
      .max(
        PASSWORD_MAX,
        t(`validation.${fieldKey}.maxLength`, { max: PASSWORD_MAX })
      )
      .regex(/[a-z]/, t(`validation.${fieldKey}.lowercase`))
      .regex(/[A-Z]/, t(`validation.${fieldKey}.uppercase`))
      .regex(/[0-9]/, t(`validation.${fieldKey}.digit`))
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        t(`validation.${fieldKey}.special`)
      )
  );

const basicPassword = (t) =>
  z.preprocess(
    str,
    z
      .string()
      .min(1, t('validation.password.required'))
      .min(
        PASSWORD_MIN,
        t('validation.password.minLength', { min: PASSWORD_MIN })
      )
      .max(
        PASSWORD_MAX,
        t('validation.password.maxLength', { max: PASSWORD_MAX })
      )
  );

const email = (t) =>
  z.preprocess(
    str,
    baseString
      .toLowerCase()
      .min(1, t('validation.email.required'))
      .pipe(z.email(t('validation.email.invalid')))
  );

const required = (t, key) => z.preprocess(str, z.string().min(1, t(key)));

// ── Schemas ─────────────────────────────────────────────────────────────────

export const loginSchema = (t) =>
  z.object({
    email: email(t),
    password: basicPassword(t),
  });

export const forgotPasswordSchema = (t) =>
  z.object({
    email: email(t),
  });

export const resetPasswordSchema = (t) =>
  z
    .object({
      newPassword: strongPassword(t, 'newPassword'),
      confirmPassword: required(t, 'validation.confirmPassword.required'),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('validation.confirmPassword.mismatch'),
      path: ['confirmPassword'],
    });

export const setupPasswordSchema = (t) =>
  z
    .object({
      tempPassword: required(t, 'validation.tempPassword.required'),
      newPassword: strongPassword(t, 'newPassword'),
      confirmPassword: required(t, 'validation.confirmPassword.required'),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('validation.confirmPassword.mismatch'),
      path: ['confirmPassword'],
    });

export const registrationSchema = (t) =>
  z
    .object({
      first_name: required(t, 'validation.firstName.required'),
      last_name: required(t, 'validation.lastName.required'),
      email: email(t),
      password: strongPassword(t, 'password'),
      confirm_password: required(t, 'validation.confirmPassword.required'),
      category_id: z.preprocess(
        (val) =>
          val === '' || val === null || val === undefined
            ? undefined
            : Number(val),
        z
          .number({ required_error: t('validation.category.required') })
          .int()
          .positive(t('validation.category.required'))
      ),
      tnc: z.boolean().refine((val) => val === true, {
        message: t('validation.tnc.required'),
      }),
    })
    .refine((data) => data.password === data.confirm_password, {
      message: t('validation.confirmPassword.mismatch'),
      path: ['confirm_password'],
    });

export const changePasswordSchema = (t, requireCurrentPassword = true) => {
  const shape = {
    newPassword: strongPassword(t, 'newPassword'),
    confirmPassword: required(t, 'validation.confirmPassword.required'),
  };

  if (requireCurrentPassword) {
    shape.currentPassword = required(t, 'validation.currentPassword.required');
  }

  return z
    .object(shape)
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('validation.confirmPassword.mismatch'),
      path: ['confirmPassword'],
    });
};
